import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { Map as ImmutableMap } from 'immutable';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { List } from 'immutable';
import { useStrategy } from './useStrategy';
import { useTranslationHelpers } from './useTranslationHelpers';
import { useDisableFields } from './useDisableFields';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { isEventBasedFilterFamily } from '../strategy/eventBasedFilters';
import { captureException } from 'raven-js';
import { useAwaitedState } from './useAwaitedState';
import { useCachedOperation } from './useCachedOperation';
import { utils } from './useFieldOptions';
import { jsx as _jsx } from "react/jsx-runtime";
const getFieldOptions = async (filterFamily, getFields, getFieldGroups, getRefinementFieldGroups, getCustomFilterFamilyFieldsGroup, strategyType, getFieldLabelString, conditionToRefineKeyPath, disableFields, disableFieldGroups) => {
  const loadedFields = await getFields(filterFamily);
  const fields = disableFields(loadedFields);
  const fieldGroups = (() => {
    let allFieldGroups;
    const customGroup = getCustomFilterFamilyFieldsGroup(filterFamily);
    if (conditionToRefineKeyPath && isEventBasedFilterFamily(filterFamily)) {
      allFieldGroups = getRefinementFieldGroups(filterFamily);
    } else if (customGroup) {
      const fieldsList = fields ? fields.toList() : List();
      allFieldGroups = List([customGroup.set('properties', fieldsList)]);
    } else {
      allFieldGroups = getFieldGroups(filterFamily);
    }
    return disableFieldGroups(allFieldGroups);
  })();
  const sortedFields = utils.getSortedFields(getFieldLabelString, fields || ImmutableMap(), filterFamily, '', undefined, undefined);
  const sortedFieldGroups = utils.getSortedFieldGroups(getFieldLabelString, fieldGroups || ImmutableMap(), filterFamily, '', undefined, undefined);
  const options = sortedFieldGroups.size > 0 ? utils.getGroupedFilteredFields(getFieldLabelString, sortedFieldGroups, '', filterFamily, strategyType) : utils.getFilteredFields(getFieldLabelString, sortedFields, '', filterFamily, strategyType);
  return options;
};
const fieldOptionCacheContext = /*#__PURE__*/createContext({
  cachedGetFieldOptions: (...args) => {
    captureException(new Error('`FieldOptionCache` not mounted'));
    return getFieldOptions(...args);
  }
});
export const FieldOptionCache = ({
  children,
  mockCachedGetFieldOptions
}) => {
  const cachedGetFieldOptions = useCachedOperation(getFieldOptions);
  return /*#__PURE__*/_jsx(fieldOptionCacheContext.Provider, {
    value: {
      cachedGetFieldOptions: mockCachedGetFieldOptions !== null && mockCachedGetFieldOptions !== void 0 ? mockCachedGetFieldOptions : cachedGetFieldOptions
    },
    children: children
  });
};

// This is outside of the render function so it is stable across renders.
//   Otherwise there will be an infinite loop.
// The calling component expects this to always be an array;
//   even while loading or on error.
const emptyPromise = Promise.resolve([]);
export const useFieldOptionsCached = filterFamily => {
  const {
    cachedGetFieldOptions
  } = useContext(fieldOptionCacheContext);
  const {
    getFields,
    getFieldGroups,
    getRefinementFieldGroups,
    getCustomFilterFamilyFieldsGroup,
    strategyType
  } = useStrategy();
  const {
    getFieldLabelString
  } = useTranslationHelpers(filterFamily);
  const {
    conditionToRefineKeyPath,
    fetchMore
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    disableFields,
    disableFieldGroups
  } = useDisableFields(filterFamily);

  // Use the promise returned by `fetchMore` to determine when it is safe to call
  //   `getFields` and `getFieldGroups`.
  //   `getFields` is async but doesn't wait for data before resolving.
  //   Neither `isLoadingFields` nor `isLoadingViewerData` are helpful indicators here.
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  useEffect(() => {
    // Some apps use the empty string as a placeholder
    //   and the existing behavior of this lib
    //   is to disregard that case
    if (filterFamily !== '') {
      fetchMore({
        variables: {
          assetFamily: DSAssetFamilies.PROPERTY_GROUP,
          ids: [filterFamily]
        }
      }).then(() => setFieldsLoaded(true));
    }
  }, [fetchMore, filterFamily]);

  // This is memoized just in case the `FieldOptionCache` is missing:
  //   If there is no cache and this isn't memoized,
  //   there will be an infinite loop.
  const promise = useMemo(() => fieldsLoaded ? cachedGetFieldOptions(filterFamily, getFields, getFieldGroups, getRefinementFieldGroups, getCustomFilterFamilyFieldsGroup, strategyType, getFieldLabelString, conditionToRefineKeyPath, disableFields, disableFieldGroups) : emptyPromise, [cachedGetFieldOptions, conditionToRefineKeyPath, disableFieldGroups, disableFields, fieldsLoaded, filterFamily, getCustomFilterFamilyFieldsGroup, getFieldGroups, getFieldLabelString, getFields, getRefinementFieldGroups, strategyType]);
  const options = useAwaitedState(promise);
  return options.state === 'fulfilled' ? options.value : [];
};